import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { RestorePasswordComponent } from "./pages/account/restore-password/restore-password/restore-password.component";
import { unsavedChangesGuard } from "./security/unsaved-changes.guard";
import { BankingOwnerInformationComponent } from "./pages/bank/banking-owner-information/banking-owner-information.component";
import { MyprofileComponent } from "./pages/myprofile/myprofile/myprofile.component";
import { CreateNewPropertyComponent } from "./pages/properties/create-new-property/create-new-property.component";
import { PropertiesDetailComponent } from "./pages/properties/properties-detail/properties-detail.component";
import { PropertiesListComponent } from "./pages/properties/properties-list/properties-list.component";
import { RegisterOwnerComponent } from "./pages/register-owner/register-owner/register-owner.component";
import { CreateAccountComponent } from "./pages/register/create-account/create-account.component";
import { RegisterStepperComponent } from "./pages/register/register-stepper/register-stepper.component";
import { AddHotelMotelComponent } from "./pages/register/register-vertical/add-hotel-motel/add-hotel-motel.component";
import { AddIndividualOwnerComponent } from "./pages/register/register-vertical/add-individual-owner/add-individual-owner.component";
import { AddPropertyManagmentCompanyComponent } from "./pages/register/register-vertical/add-property-managment-company/add-property-managment-company.component";
import { CodeConfirmationComponent } from "./security/code-confirmation/code-confirmation.component";
import { CreateNewPasswordComponent } from "./security/create-new-password/create-new-password.component";
import { LoginComponent } from "./security/login/login.component";
import { SetPasswordComponent } from "./security/set-password/set-password.component";
import { AnonymousLayoutComponent } from "./shared/anonymous-layout/anonymous-layout.component";
import { ProfileDetailComponent } from "./pages/profile/profile-detail/profile-detail.component";
import { PrintPageComponent } from "./pages/register/register-vertical/print-page/print-page.component";
import { HomeComponent } from "./pages/home/home.component";
import { WelcomeComponent } from "./pages/home/welcome/welcome.component";
import { InboxComponent } from "./pages/message/inbox/inbox.component";
import { CertificateOfRegistrationComponent } from "./pages/reports/certificate-of-registration/certificate-of-registration.component";
import { authGuard } from "./security/authGuard/auth-guard";
import { biometricLockGuard } from "./core/native/biometric-lock.guard";
import { LockComponent } from "./core/native/lock/lock.component";
import { CheckoutReturnComponent } from "./core/native/checkout-return/checkout-return.component";
import { PaymentsDashboardComponent } from "./pages/payments/payments-dashboard/payments-dashboard.component";
import { MakePaymentComponent } from "./pages/payments/make-payment/make-payment.component";
import { ReturnDetailComponent } from "./pages/payments/return-detail/return-detail.component";
import { PaymentHistoryComponent } from "./pages/payments/payment-history/payment-history.component";
import { PaymentConfirmationComponent } from "./pages/payments/payment-confirmation/payment-confirmation.component";

const routes: Routes = [
  {
    path: "app",
    component: HomeComponent,
    data: { breadcrumb: "Home" },
    canActivate: [authGuard, biometricLockGuard],
    children: [
      {
        path: "",
        component: WelcomeComponent,
        pathMatch: "full",
      },
      {
        path: "BankOwnerInformation",
        component: BankingOwnerInformationComponent,
      },

      {
        path: "properties",
        component: PropertiesListComponent,
        data: {
          showFather: false,
          breadcrumb: "Properties",
          svgIcon: "maps_home_work",
          label: "+ Add new property",
          url: "/app/addNewProperty",
        },
      
      },
      {
        path: "addNewProperty",
        component: CreateNewPropertyComponent,
        data: { breadcrumb: "Add new property", icon: "add" },
      },
      {
        path: "properties/propertyDetail/:id",
        component: PropertiesDetailComponent,
        data: { breadcrumb: "Property Detail", icon: "description" },
      },

      {
        path: "profile",
        component: ProfileDetailComponent,
        data: { breadcrumb: "Profile", icon: "account_circle" },
      },
      {
        path: "messages",
        component: InboxComponent,
        data: { breadcrumb: "Messages", icon: "mail" },
      },

      // Payment routes
      {
        path: "payments",
        component: PaymentsDashboardComponent,
        data: { breadcrumb: "Payments", icon: "payment" },
      },
      {
        path: "payments/make-payment",
        component: MakePaymentComponent,
        data: { breadcrumb: "Make a Payment", icon: "credit_card" },
      },
      {
        path: "payments/return/:id",
        component: ReturnDetailComponent,
        data: { breadcrumb: "Return", icon: "receipt_long" },
      },
      {
        path: "payments/history",
        component: PaymentHistoryComponent,
        data: { breadcrumb: "Payment History", icon: "history" },
      },
      {
        path: "payments/confirmation/:confirmationNumber",
        component: PaymentConfirmationComponent,
        data: { breadcrumb: "Payment Confirmation", icon: "check_circle" },
      },
      {
        // Hosted-checkout return URL (Catalis redirect): no confirmationNumber yet, just
        // ?ref=<paymentId>&transactionId=<gatewayTxn> — the component completes the checkout.
        path: "payments/confirmation",
        component: PaymentConfirmationComponent,
        data: { breadcrumb: "Payment Confirmation", icon: "check_circle" },
      }

    ],
  },
  {
    path: "reports/CertificateOfRegistration/:id",
    component: CertificateOfRegistrationComponent,
    data: { breadcrumb: "reports", icon: "description" },
  },
  {
    path: "register",
    component: AnonymousLayoutComponent,
    children: [
      {
        path: "registerAsOwnerBeDelete",
        component: RegisterOwnerComponent,
        data: { breadcrumb: "" },
      },
      {
        path: "createAccount",
        component: CreateAccountComponent,
        data: { breadcrumb: "createAccount" },
      },
      {
        path: "registerAsOwner",
        component: AddIndividualOwnerComponent,
        data: { breadcrumb: "Add new property" },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: "registerAsPropertyManagmentCompany",
        component: AddPropertyManagmentCompanyComponent,
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: "registerAsHotelMotel",
        component: AddHotelMotelComponent,
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: "createAccount",
        component: RegisterStepperComponent,
      },
      {
        path: "printLegalAgreement",
        component: PrintPageComponent,
      },
      
    ],
  },
  {
    path: "forgotPassword",
    component: RestorePasswordComponent,
  },
  {
    path: "login",
    component: LoginComponent,
  },
  {
    path: "lock",
    component: LockComponent,
  },
  {
    // Public gateway landing page for hosted checkouts started from the app.
    path: "checkout-return",
    component: CheckoutReturnComponent,
  },
  {
    path: "setNewPassword/:email",
    component: SetPasswordComponent,
  },
  {
    path: "codeVerification/:email",
    component: CodeConfirmationComponent,
  },
  {
    path: "createPassword",
    component: CreateNewPasswordComponent,
  },
  {
    // Open the app at the authenticated area; authGuard sends anyone without
    // a valid session on to /login. Avoids showing the login screen to users
    // who are already signed in (e.g. a restored native session).
    path: "",
    pathMatch: "full",
    redirectTo: "app",
  },
  {
    path: "**",
    redirectTo: "login",
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
